import { extractRecipeSizeLabel } from "@/lib/manufacturing/groupMembers";

export type SeriesRenameMember = {
  id: string;
  code: string;
  name: string;
};

/**
 * Guess the code/name find strings for retitling members when renaming a series.
 * Prefers the current group identity when it already appears on members; otherwise
 * falls back to common wizard mistake prefixes (SERIES / Series).
 */
export function defaultSeriesMemberRenameFinds(
  group: { code: string; name: string },
  members: readonly SeriesRenameMember[],
): { codeFind: string; nameFind: string } {
  const codeFindDefault = group.code.trim() || "SERIES";
  const nameFindDefault = group.name.trim() || "Series";
  if (members.length === 0) {
    return { codeFind: codeFindDefault, nameFind: nameFindDefault };
  }

  const codeNeedle = group.code.trim().toLowerCase();
  const nameNeedle = group.name.trim().toLowerCase();
  const codeHits = codeNeedle
    ? members.filter((m) => m.code.toLowerCase().includes(codeNeedle)).length
    : 0;
  const nameHits = nameNeedle
    ? members.filter((m) => m.name.toLowerCase().includes(nameNeedle)).length
    : 0;

  let codeFind = codeFindDefault;
  if (codeHits === 0) {
    const seriesHits = members.filter((m) =>
      m.code.toUpperCase().includes("SERIES"),
    ).length;
    if (seriesHits > 0) codeFind = "SERIES";
    else {
      const inferred = inferCommonCodePrefix(members);
      if (inferred) codeFind = inferred;
    }
  }

  let nameFind = nameFindDefault;
  if (nameHits === 0) {
    const seriesNameHits = members.filter((m) =>
      m.name.toLowerCase().includes("series"),
    ).length;
    if (seriesNameHits > 0) nameFind = "Series";
  }

  return { codeFind, nameFind };
}

function inferCommonCodePrefix(members: readonly SeriesRenameMember[]): string | null {
  const counts = new Map<string, number>();
  for (const m of members) {
    const size = extractRecipeSizeLabel(m);
    let prefix = "";
    if (size) {
      const suffix = `-${size}`;
      if (m.code.toLowerCase().endsWith(suffix.toLowerCase())) {
        prefix = m.code.slice(0, m.code.length - suffix.length);
      }
    }
    if (!prefix) {
      const i = m.code.lastIndexOf("-");
      if (i > 0) prefix = m.code.slice(0, i);
    }
    if (!prefix) continue;
    counts.set(prefix, (counts.get(prefix) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [prefix, n] of counts) {
    if (n > bestN) {
      best = prefix;
      bestN = n;
    }
  }
  return bestN > 0 ? best : null;
}
